/**
 * The coverage diagram: five measured regions on a stylised head.
 *
 * This is a **schematic**, and the label under it says so. It is not the
 * user's reconstruction, it is not a 3D model, and it is not derived from
 * their photographs - it is a fixed vector drawing whose five areas are
 * coloured by the five region statuses the backend measured. The mockup's
 * rendered mesh head would have implied all three of those things.
 *
 * Drawn as SVG rather than imported as a model: a real head asset would be
 * hundreds of kilobytes, would need a renderer on a screen that has no other
 * use for one, and - if it were a scan of a person - would put someone's face
 * in the repository. The shapes here are geometry, not anybody.
 *
 * Colour is never the only channel. Each region is a real `<button>` carrying
 * its name and status as text for a screen reader, the selected region gets a
 * visible outline, and the list beside the diagram repeats every value in
 * words. A user who cannot distinguish the greens from the reds loses nothing.
 */

import type { CoverageRegion, CoverageStatus } from "@/api/types";
import { COVERAGE_REGION_LABELS, COVERAGE_STATUS_TERMS } from "@/i18n/terms";
import "./coverage-diagram.css";

/**
 * One outline per measured region, in a 320x360 viewBox.
 *
 * Deliberately schematic - a front-facing head with the two profile bands at
 * the sides and the ears outboard of them. The five shapes correspond exactly
 * to the five regions the backend measures; there is no sixth shape, because
 * there is no sixth measurement.
 */
const SHAPES: Record<CoverageRegion, string> = {
  // Face: an oval from brow to chin, narrowing at the jaw - the area a face
  // landmarker actually covers.
  front_face:
    "M160 74c30 0 50 20 53 52 2 24 0 48-6 70-6 22-15 40-26 50-7 6-14 9-21 9s-14-3-21-9c-11-10-20-28-26-50-6-22-8-46-6-70 3-32 23-52 53-52Z",
  // Profile bands: the cheek-to-temple strip either side of the face, curving
  // with the skull rather than sitting as straight slabs.
  left_side:
    "M107 84c-8 12-12 30-12 52 0 30 5 60 15 82 4 9 9 16 15 21-11-2-20-10-27-24-11-22-16-52-16-82 0-24 6-42 17-52 2-2 5-1 8 3Z",
  right_side:
    "M213 84c8 12 12 30 12 52 0 30-5 60-15 82-4 9-9 16-15 21 11-2 20-10 27-24 11-22 16-52 16-82 0-24-6-42-17-52-2-2-5-1-8 3Z",
  // Ears: outboard of the profile bands, at temple height.
  left_ear:
    "M83 148c-8 1-12 10-11 23 1 15 6 29 13 36 5 5 10 4 12-2 3-9 4-25 2-40-2-12-7-18-16-17Z",
  right_ear:
    "M237 148c8 1 12 10 11 23-1 15-6 29-13 36-5 5-10 4-12-2-3-9-4-25-2-40 2-12 7-18 16-17Z",
};

/**
 * Cranium, neck and shoulders. Drawn grey, never interactive: this version
 * measures none of them, and the caption below the diagram says so.
 */
const UNMEASURED_CRANIUM =
  "M160 28c50 0 84 32 86 80 1 12 0 22-2 30-3-30-11-52-26-66-15-14-35-21-58-21s-43 7-58 21c-15 14-23 36-26 66-2-8-3-18-2-30 2-48 36-80 86-80Z";
const UNMEASURED_NECK =
  "M128 258c5 16 6 28 4 38h56c-2-10-1-22 4-38-10 10-21 16-32 16s-22-6-32-16Z";

interface Props {
  /** Only regions the backend returned. A region absent here is not drawn as measured. */
  statuses: Partial<Record<CoverageRegion, CoverageStatus>>;
  selected: CoverageRegion | null;
  onSelect: (region: CoverageRegion) => void;
}

export function CoverageDiagram({ statuses, selected, onSelect }: Props) {
  const regions = Object.keys(SHAPES) as CoverageRegion[];

  return (
    <figure className="diagram">
      <svg
        viewBox="0 0 320 360"
        className="diagram__svg"
        role="group"
        aria-label="Kapsama şeması"
      >
        {/* Areas this version does not measure at all. Grey, inert, and
            labelled below the diagram - not a status, an absence of one. */}
        <path d={UNMEASURED_CRANIUM} className="diagram__unmeasured" />
        <path d={UNMEASURED_NECK} className="diagram__unmeasured" />

        {regions.map((region) => {
          const status = statuses[region];
          const term = status ? COVERAGE_STATUS_TERMS[status] : null;
          const isSelected = selected === region;
          return (
            <g key={region}>
              <path
                d={SHAPES[region]}
                className={[
                  "diagram__region",
                  status ? `diagram__region--${status}` : "diagram__region--absent",
                  isSelected ? "diagram__region--selected" : "",
                ]
                  .join(" ")
                  .trim()}
                onClick={() => onSelect(region)}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                // The accessible name carries the status in words, so the
                // colour is never the only way to read this.
                aria-label={`${COVERAGE_REGION_LABELS[region]}: ${
                  term ? term.label : "ölçülmedi"
                }`}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(region);
                  }
                }}
              />
            </g>
          );
        })}
      </svg>
      <figcaption className="diagram__caption">Kapsama şeması — 3D model değildir.</figcaption>
    </figure>
  );
}
